'use client';

import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { BaseLinePlay2 } from '@/app/components/icons/BaseLineCirclePlay';
import type { AudioPlayButtonRuntimeProps } from './AudioPlayButtonRuntime';

interface AudioPlayButtonProps {
  audioUrl: string;
  audioId: string;
}

const AudioPlayButtonRuntime = dynamic(
  () => import('./AudioPlayButtonRuntime').then((mod) => mod.AudioPlayButtonRuntime),
  {
    ssr: false,
    loading: () => <StaticPlayButton />,
  },
);

function StaticPlayButton({ onClick }: { onClick?: () => void }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <button
        type="button"
        onClick={onClick}
        className="flex h-12 w-12 items-center justify-center text-white transition-all hover:scale-110"
        aria-label="Play sound preview"
      >
        <BaseLinePlay2 className="h-full w-full" />
      </button>
    </div>
  );
}

export function AudioPlayButton({ audioUrl, audioId }: AudioPlayButtonProps) {
  const [shouldLoadRuntime, setShouldLoadRuntime] = useState(false);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (shouldLoadRuntime) {
      return;
    }

    const target = wrapperRef.current;
    if (!target) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      setShouldLoadRuntime(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShouldLoadRuntime(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [shouldLoadRuntime]);

  const handleStaticClick = () => {
    setShouldAutoPlay(true);
    setShouldLoadRuntime(true);
  };

  const runtimeProps: AudioPlayButtonRuntimeProps = {
    audioUrl,
    audioId,
    autoPlay: shouldAutoPlay,
  };

  return (
    <div ref={wrapperRef} className="absolute inset-0">
      {shouldLoadRuntime ? (
        <AudioPlayButtonRuntime {...runtimeProps} />
      ) : (
        <StaticPlayButton onClick={handleStaticClick} />
      )}
    </div>
  );
}
